import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  MapPin,
  Phone,
  Mail,
  Twitter,
  Building2,
  Home,
  Maximize,
  Globe,
  DollarSign,
  BookOpen,
  X,
  ArrowUp,
  Send,
  Loader
} from 'lucide-react';
import { FaInstagram, FaTiktok, FaWhatsapp } from 'react-icons/fa';
import tinycolor from 'tinycolor2';
import networkService from '../services/networkService';
import './PublicMarketerLanding.css';
import { buildOfferShareUrl } from '../utils/offerShareUtils';

/* ─── Custom hook: IntersectionObserver ────────────────────────────────────── */
function useFadeUp(threshold = 0.15) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('is-visible'); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
}

/* ─── AnimatedNumber ────────────────────────────────────────────────────────── */
function AnimatedNumber({ target, suffix = '' }) {
  const [current, setCurrent] = useState(0);
  const [triggered, setTriggered] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !triggered) { setTriggered(true); observer.disconnect(); } },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [triggered]);

  useEffect(() => {
    if (!triggered) return;
    let start = 0;
    const duration = 1200;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCurrent(target); clearInterval(timer); }
      else setCurrent(start);
    }, 16);
    return () => clearInterval(timer);
  }, [triggered, target]);

  return (
    <span ref={ref} className={`pml-stat-num${triggered ? ' is-animated' : ''}`}>
      {suffix === '+' ? `+${current}` : current}
    </span>
  );
}


/* ─── Offer type → Arabic filter label ─────────────────────────────────────── */
function offerTypeLabel(propertyType) {
  if (!propertyType) return '';
  const map = {
    villa: 'فيلا',
    duplex: 'دوبلكس',
    floor: 'دور',
    apartment: 'شقة',
    land: 'أرض',
    commercial: 'تجاري',
    building: 'عمارة',
    warehouse: 'مستودع',
    farm: 'مزرعة',
    chalet: 'شاليه'
  };
  return map[propertyType] || propertyType;
}

const getSafeAvatarSrc = (image) => {
  if (!image || typeof image !== 'string') return null;
  const value = image.trim();
  return /^(https?:|data:image\/|blob:|\/)/i.test(value) ? value : null;
};

const LANDING_THEME_COLORS = {
  neutral: {
    gold: '#14532D',
    dark: '#111827',
    sand: '#F8FAFC',
    white: '#FFFFFF',
    text: '#111827',
    muted: '#6B7280',
    border: '#E5E7EB'
  },
  classic: {
    gold: '#C5A880',
    dark: '#2C1810',
    sand: '#F5F0E8',
    white: '#FFFFFF',
    text: '#333333',
    muted: '#888888',
    border: '#e5e2dc'
  }
};

const REQUEST_PROPERTY_TYPES = [
  { value: 'villa', label: 'فيلا' },
  { value: 'apartment', label: 'شقة' },
  { value: 'land', label: 'أرض' },
  { value: 'building', label: 'عمارة' },
  { value: 'duplex', label: 'دوبلكس' },
  { value: 'floor', label: 'دور' },
  { value: 'shop', label: 'محل' },
  { value: 'warehouse', label: 'مستودع' },
  { value: 'studio', label: 'استوديو' },
  { value: 'any', label: 'لا يهم' }
];

const initialPropertyRequest = {
  clientName: '',
  clientPhone: '',
  requestType: 'buy',
  propertyType: 'villa',
  city: '',
  district: '',
  budget: '',
  areaMin: '',
  notes: ''
};

const normalizeSaudiPhone = (phone) => {
  // نفس فكرة تنسيق رقم السعودية في صفحات النظام: نخزن الرقم بصيغة محلية نظيفة.
  let normalizedPhone = String(phone || '').replace(/[\s()-]/g, '');
  if (normalizedPhone.startsWith('00966')) normalizedPhone = '0' + normalizedPhone.slice(5);
  else if (normalizedPhone.startsWith('+966')) normalizedPhone = '0' + normalizedPhone.slice(4);
  else if (normalizedPhone.startsWith('966')) normalizedPhone = '0' + normalizedPhone.slice(3);
  else if (normalizedPhone.startsWith('5')) normalizedPhone = '0' + normalizedPhone;
  return normalizedPhone;
};

function getLandingThemeStyles(theme = 'neutral', brandColor) {
  const normalizedTheme = ['neutral', 'classic', 'custom'].includes(theme) ? theme : 'neutral';
  const safeBrandColor = tinycolor(brandColor).isValid() && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(String(brandColor || '').trim())
    ? String(brandColor).trim()
    : null;
  const palette = normalizedTheme === 'classic'
    ? LANDING_THEME_COLORS.classic
    : LANDING_THEME_COLORS.neutral;
  const primary = normalizedTheme === 'custom' && safeBrandColor
    ? (tinycolor(safeBrandColor).isLight() ? tinycolor(safeBrandColor).darken(32).toHexString() : safeBrandColor)
    : palette.gold;
  const c = tinycolor(primary);
  const { r, g, b } = c.toRgb();
  return {
    '--pml-gold': primary,
    '--pml-gold-07': `rgba(${r},${g},${b},0.07)`,
    '--pml-gold-15': `rgba(${r},${g},${b},0.15)`,
    '--pml-gold-20': `rgba(${r},${g},${b},0.20)`,
    '--pml-gold-25': `rgba(${r},${g},${b},0.25)`,
    '--pml-gold-50': `rgba(${r},${g},${b},0.5)`,
    '--pml-gold-shadow': `0 8px 24px rgba(${r},${g},${b},0.4)`,
    '--pml-gold-20-border': `rgba(${r},${g},${b},0.2)`,
    // Keep text/background stable; custom logo color is used as accents and buttons only.
    '--pml-dark': palette.dark,
    '--pml-sand': palette.sand,
    '--pml-white': palette.white,
    '--pml-text': palette.text,
    '--pml-muted': palette.muted,
    '--pml-border': palette.border
  };
}

/* ─── Main Component ────────────────────────────────────────────────────────── */
function normalizeExternalUrl(value, baseUrl) {
  if (!value) return '';
  const trimmed = String(value).trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.includes('.')) return `https://${trimmed}`;
  const handle = trimmed.replace(/^@/, '');
  return baseUrl ? `${baseUrl}${handle}` : `https://${trimmed}`;
}

const normalizeContactPhone = (phone) => {
  // Extra contacts can arrive in local or international Saudi format.
  let normalizedPhone = String(phone || '').replace(/[\s()-]/g, '');
  if (normalizedPhone.startsWith('00966')) normalizedPhone = '0' + normalizedPhone.slice(5);
  else if (normalizedPhone.startsWith('+966')) normalizedPhone = '0' + normalizedPhone.slice(4);
  else if (normalizedPhone.startsWith('966')) normalizedPhone = '0' + normalizedPhone.slice(3);
  else if (normalizedPhone.startsWith('5')) normalizedPhone = '0' + normalizedPhone;
  return normalizedPhone;
};

const getWhatsappPhone = (phone) => {
  const localPhone = normalizeContactPhone(phone);
  return localPhone.startsWith('05') ? `966${localPhone.slice(1)}` : localPhone;
};

const getTelPhone = (phone) => {
  const localPhone = normalizeContactPhone(phone);
  return localPhone.startsWith('0') ? `+966${localPhone.slice(1)}` : localPhone;
};

export default function PublicMarketerLanding({ previewData = null, isPreview = false }) {
  const { username } = useParams();
  const navigate = useNavigate();
  // Build catalog links inside the marketer page, with a fallback for preview mode.
  const getMarketerCatalogPath = (catalog, offerId) => {
    const basePath = username ? `/p/${username}/catalog/${catalog.slug}` : `/c/${catalog.slug}`;
    return offerId
      ? buildOfferShareUrl({ username, catalogSlug: catalog.slug, offerId })
      : basePath;
  };

  const [marketer, setMarketer] = useState(previewData?.marketer || null);
  const [offers, setOffers] = useState(previewData?.offers || []);
  const [catalogs, setCatalogs] = useState(previewData?.catalogs || []);
  const [loading, setLoading] = useState(!previewData);
  const [notFound, setNotFound] = useState(false);
  const [showPreviewBanner, setShowPreviewBanner] = useState(isPreview);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [propertyRequest, setPropertyRequest] = useState(initialPropertyRequest);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestResult, setRequestResult] = useState(null);

  useEffect(() => {
    if (previewData) {
      // Preview mode uses the same page component with mock data instead of API data.
      setMarketer(previewData.marketer || null);
      setOffers(previewData.offers || []);
      setCatalogs(previewData.catalogs || []);
      setLoading(false);
      setNotFound(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await networkService.getMarketerProfile(username);
        setMarketer(data.data.marketer);
        setOffers(data.data.offers || []);
        setCatalogs(data.data.catalogs || []);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    if (username) fetchProfile();
  }, [username, previewData]);

  useEffect(() => {
    // Try a new avatar URL again, then fall back to the first letter only if it fails.
    setAvatarFailed(false);
  }, [marketer?.avatarUrl, marketer?.avatar]);

  const availableFilters = useMemo(() => {
    // Filter chips store raw propertyType values so filtering does not depend on Arabic labels.
    const types = new Set(offers.map(o => o?.propertyType).filter(Boolean));
    return [{ value: 'all', label: 'الكل' }, ...Array.from(types).map(type => ({ value: type, label: offerTypeLabel(type) }))];
  }, [offers]);

  const [activeFilter, setActiveFilter] = useState('all');
  const filteredProps = activeFilter === 'all'
    ? offers
    : offers.filter(p => p?.propertyType === activeFilter);

  useEffect(() => {
    // Reset to all if the selected raw type no longer exists in the current offers.
    if (activeFilter !== 'all' && !availableFilters.some(filter => filter.value === activeFilter)) {
      setActiveFilter('all');
    }
  }, [activeFilter, availableFilters]);

  /* Navbar scroll state */
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);


  /* Scroll animation observer */
  useEffect(() => {
    if (!marketer) return;
    let observer;

    // عناصر الـ Hero تظهر فوراً
    document.querySelectorAll('.pml-hero-content .pml-animate').forEach(el => {
      el.classList.add('pml-visible');
    });

    const timer = setTimeout(() => {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              // Reveal each animated element once, then stop watching it to avoid flicker.
              entry.target.classList.add('pml-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.05, rootMargin: '0px 0px -30px 0px' }
      );

      document.querySelectorAll('.pml-animate:not(.pml-visible)').forEach((el) => {
        observer.observe(el);
      });

      // Fallback for elements already in viewport
      document.querySelectorAll('.pml-animate:not(.pml-visible)').forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.classList.add('pml-visible');
          observer.unobserve(el);
        }
      });
    }, 500);

    return () => {
      clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  }, [marketer, offers, catalogs]);

  /* عنوان المتصفح — اسم المسوق */
  useEffect(() => {
    if (marketer?.name) {
      document.title = `${marketer.name} — مسوق عقاري | Misk`;
      return () => { document.title = 'Misk'; };
    }
  }, [marketer?.name]);

  /* Fade-up refs */
  const aboutRef   = useFadeUp();
  const statsRef   = useFadeUp();
  const specRef    = useFadeUp();
  const catalogRef = useFadeUp();
  const propsRef   = useFadeUp();
  const contactRef = useFadeUp();

  /* Smooth scroll helper */
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  /* Specialty data check */
  const hasSpecialtyData = marketer?.specialty || marketer?.districts || marketer?.languages ||
    (marketer?.priceRangeMin > 0) || (marketer?.priceRangeMax > 0);

  /* Hero trust line: show only real trust data when the marketer keeps this hero toggle enabled. */
  const heroTrustItems = marketer?.showHeroTrustLine === false ? [] : [
    marketer?.falLicense ? 'رخصة فال' : null,
    marketer?.experience > 0 ? `${marketer.experience} سنوات خبرة` : null,
    marketer?.dealsDone > 0 ? `${marketer.dealsDone}+ صفقة` : null,
  ].filter(Boolean);

  /* Hero badge: Settings controls specialty; profile controls the city. */
  const heroBadgeText = [marketer?.specialty, marketer?.city].filter(Boolean).join(' · ');
  // Prefer the signed hero URL, and only use heroImage directly when it is already a browser-ready URL.
  const customHeroImageSource = marketer?.heroImageUrl || (
    /^https?:\/\//.test(marketer?.heroImage || '') ? marketer.heroImage : null
  );
  const marketerAvatarSrc = !avatarFailed ? getSafeAvatarSrc(marketer?.avatarUrl || marketer?.avatar) : null;
  const heroOffersCount = marketer?.totalOffers > 0 ? marketer.totalOffers : offers.length;
  const heroBrowseText = heroOffersCount > 0 ? `تصفح العقارات (${heroOffersCount})` : 'تصفح جميع العروض';
  // Keep the consultation action disabled for now without adding a new setting.
  const showHeroConsultButton = false;
  const heroIntroText = marketer?.heroIntroText?.trim() || '';
  /* Services section only uses real saved services and hides empty values. */
  const marketerServices = Array.isArray(marketer?.services)
    ? marketer.services.filter(service => typeof service === 'string' && service.trim())
    : [];
  const getServiceWhatsappLink = (service) => (
    marketer?.whatsapp
      ? `https://wa.me/${marketer.whatsapp}?text=${encodeURIComponent(`السلام عليكم، أرغب بالاستفسار عن خدمة: ${service}`)}`
      : ''
  );
  const aboutBio = marketer?.bio?.trim() || 'مسوق عقاري يقدم خدماته باحترافية ووضوح.';
  const allOffersCatalog = catalogs.find(c => c.name === 'كافة العروض') || catalogs.find(c => c.isDefault) || catalogs[0];
  const activeLandingTheme = ['neutral', 'classic'].includes(marketer?.landingTheme)
    ? marketer.landingTheme
    : 'neutral';
  const landingThemeStyles = getLandingThemeStyles(activeLandingTheme, marketer?.brandColor);
  const heroImageMode = marketer?.heroImageMode || 'default';
  const heroImageSource = heroImageMode === 'custom' && customHeroImageSource
    ? customHeroImageSource
    : null;
  const heroBackgroundImage = heroImageSource
    ? `url('${heroImageSource}')`
    : heroImageMode === 'none'
      ? 'linear-gradient(135deg, var(--pml-dark) 0%, var(--pml-gold) 48%, var(--pml-sand) 100%)'
      : "url('https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=80')";
  const customCatalogs = catalogs.filter(c => c.name !== 'كافة العروض' && !c.isDefault);
  // Respect the two existing visibility fields behind the unified settings toggle.
  const showCatalogsInAvailableProperties = marketer?.showCatalogsSection !== false;
  const showOffersInAvailableProperties = marketer?.showOffersSection !== false;
  const hasAvailablePropertiesData =
    (showCatalogsInAvailableProperties && customCatalogs.length > 0) ||
    (showOffersInAvailableProperties && offers.length > 0);
  // Contact visibility is shared by Navbar and Contact so no link points to a missing section.
  const hasPhone = Boolean(marketer?.phone);
  const hasEmail = Boolean(marketer?.email);
  const hasTwitter = Boolean(marketer?.twitter);
  const hasInstagram = Boolean(marketer?.instagram);
  const hasTiktok = Boolean(marketer?.tiktok);
  const hasExtraLink = Boolean(marketer?.extraLink);
  const whatsappGroupUrl = normalizeExternalUrl(marketer?.whatsappGroupUrl);
  const whatsappGroupLabel = String(marketer?.whatsappGroupLabel || '').trim() || 'انضم لقروب العروض';
  const hasWhatsappGroup = Boolean(whatsappGroupUrl);
  const mainPhoneKey = normalizeContactPhone(marketer?.phone);
  // Only show public-site extra contacts, and avoid repeating the main profile phone.
  const publicAdditionalContacts = Array.isArray(marketer?.additionalContactNumbers)
    ? marketer.additionalContactNumbers.filter(contact => (
      contact?.showInPublicSite !== false &&
      contact?.phone &&
      normalizeContactPhone(contact.phone) !== mainPhoneKey
    ))
    : [];
  const hasContactSection = marketer?.showContactSection !== false && (
    hasPhone || hasEmail || hasTwitter || hasInstagram || hasTiktok || hasExtraLink || hasWhatsappGroup || publicAdditionalContacts.length > 0
  );
  const totalStatsOffers = marketer?.totalOffers > 0 ? marketer.totalOffers : offers.length;
  // Stats are optional and only show when at least two real positive numbers exist.
  const visibleStats = [
    totalStatsOffers > 0 ? { key: 'offers', target: totalStatsOffers, suffix: '', label: 'عقار نشط' } : null,
    catalogs.length > 0 ? { key: 'catalogs', target: catalogs.length, suffix: '', label: 'كتالوج' } : null,
    marketer?.experience > 0 ? { key: 'experience', target: marketer.experience, suffix: '', label: 'سنوات خبرة' } : null,
    marketer?.dealsDone > 0 ? { key: 'dealsDone', target: marketer.dealsDone, suffix: '+', label: 'صفقة منجزة' } : null,
  ].filter(Boolean);
  const hasAboutSection = marketer?.showAboutSection !== false;
  const hasServicesSection = marketerServices.length > 0;
  const hasSpecialtySection = marketer?.showSpecialtySection !== false && hasSpecialtyData;
  const hasStatsSection = marketer?.showStatsSection !== false && visibleStats.length >= 2;
  // Header links follow the exact visible section order on the public page.
  const navSections = [
    hasAboutSection ? { id: 'about', label: 'من أنا' } : null,
    hasServicesSection ? { id: 'services', label: 'الخدمات' } : null,
    hasAvailablePropertiesData ? { id: 'properties', label: 'العقارات' } : null,
    hasSpecialtySection ? { id: 'specialty', label: 'نطاق العمل' } : null,
    hasStatsSection ? { id: 'stats', label: 'الإحصاءات' } : null,
    hasContactSection ? { id: 'contact', label: 'تواصل' } : null,
  ].filter(Boolean);
  const firstVisibleSectionId = navSections[0]?.id;
  const catalogHasOffer = (catalog, offer) => {
    if (!catalog || !offer) return false;
    const offerId = String(offer._id || offer.id || '');
    const catalogOffers = catalog.offers || catalog.offerIds || catalog.offerIdsList || [];
    return Array.isArray(catalogOffers) && catalogOffers.some(item => String(item?._id || item?.id || item) === offerId);
  };
  const getCatalogForOffer = (offer) => {
    // Prefer the all/default catalog, then use offer membership if future API data includes it.
    return catalogs.find(c => c.name === 'كافة العروض')
      || catalogs.find(c => c.isDefault)
      || catalogs.find(c => catalogHasOffer(c, offer))
      || catalogs[0]
      || null;
  };
  const requestCityOptions = Array.from(new Set([
    marketer?.city,
    ...offers.map(offer => offer?.city).filter(Boolean)
  ].filter(Boolean)));
  const requestSuccessMessage = requestResult?.matchingOffers?.length > 0
    ? 'تم تسجيل طلبك، وهذه بعض العروض التي قد تناسبك. سنتواصل معك في أقرب وقت.'
    : 'تم تسجيل طلبك، وسنتواصل معك عند توفر عرض مناسب.';
  const handleOpenRequestModal = () => {
    // نعيد النموذج لحالة واضحة حتى لا تختلط بيانات زائر سابق بزائر جديد.
    setPropertyRequest({ ...initialPropertyRequest, city: marketer?.city || '' });
    setRequestResult(null);
    setRequestError('');
    setShowRequestModal(true);
  };
  const handleCloseRequestModal = () => {
    setShowRequestModal(false);
    setRequestSubmitting(false);
  };
  const handlePropertyRequestChange = (field, value) => {
    setPropertyRequest(prev => ({ ...prev, [field]: value }));
    setRequestError('');
  };
  const handleSubmitPropertyRequest = async (event) => {
    event.preventDefault();
    if (requestSubmitting || isPreview) return;
    const cleanPhone = normalizeSaudiPhone(propertyRequest.clientPhone);
    const requiredValues = [
      propertyRequest.clientName,
      cleanPhone,
      propertyRequest.requestType,
      propertyRequest.propertyType,
      propertyRequest.city,
      propertyRequest.budget
    ];
    if (requiredValues.some(value => !String(value || '').trim())) {
      setRequestError('يرجى تعبئة الحقول المطلوبة قبل الإرسال.');
      return;
    }
    try {
      setRequestSubmitting(true);
      const response = await networkService.submitMarketerRequest(username, {
        ...propertyRequest,
        clientPhone: cleanPhone,
        budget: String(propertyRequest.budget).replace(/,/g, ''),
        areaMin: String(propertyRequest.areaMin || '').replace(/,/g, '')
      });
      setRequestResult(response.data || { matchingOffers: [] });
    } catch (error) {
      setRequestError(error.message || 'تعذر إرسال الطلب. حاول مرة أخرى.');
    } finally {
      setRequestSubmitting(false);
    }
  };

  /* ── Loading / Not Found screens ─────────────────────────────────────────── */
  let visibleMiddleSectionIndex = 0;
  const getMiddleSectionToneClass = () => {
    // Give only visible middle sections alternating backgrounds in their real render order.
    const toneClass = visibleMiddleSectionIndex % 2 === 0 ? 'pml-section-light' : 'pml-section-soft';
    visibleMiddleSectionIndex += 1;
    return toneClass;
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#2C1810' }}>
      <div style={{ color: '#C5A880', fontSize: '1.2rem' }}>جاري التحميل...</div>
    </div>
  );

  if (notFound) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#2C1810' }}>
      <div style={{ color: '#C5A880', fontSize: '1.2rem' }}>المسوق غير موجود</div>
    </div>
  );

  return (
    <div className={`pml-page pml-theme-${activeLandingTheme}`} dir="rtl" style={landingThemeStyles}>

      {/* Preview banner lives here so preview and public pages share one layout. */}
      {isPreview && showPreviewBanner && (
        <div className="pml-preview-banner">
          <span>هذه معاينة توضيحية — أضف بياناتك من الإعدادات لتظهر صفحتك الحقيقية</span>
          <div className="pml-preview-banner-actions">
            <Link to="/settings" className="pml-preview-settings-link">
              إعداد صفحتي
            </Link>
            <button
              onClick={() => setShowPreviewBanner(false)}
              className="pml-preview-close"
              aria-label="إغلاق"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* ── Navbar ─────────────────────────────────────────────────────────── */}
      <nav
        className={`pml-nav${scrolled ? ' pml-nav--scrolled' : ''}`}
        style={{ top: isPreview && showPreviewBanner ? 49 : 0 }}
      >
        <div className="pml-nav-brand">
          <div className="pml-nav-avatar">
            {marketerAvatarSrc ? (
              <img src={marketerAvatarSrc} alt={marketer?.name || 'الشعار أو صورة الحساب'} onError={() => setAvatarFailed(true)} />
            ) : (
              marketer?.name?.charAt(0)
            )}
          </div>
          <span className="pml-nav-name">{marketer?.name}</span>
        </div>

        <ul className="pml-nav-links">
          {navSections.map(section => (
            <li key={section.id}>
              <a href={`#${section.id}`} onClick={e => { e.preventDefault(); scrollTo(section.id); }}>
                {section.label}
              </a>
            </li>
          ))}
        </ul>

        {Boolean(marketer?.whatsapp) && (
          <a
            href={`https://wa.me/${marketer.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="pml-nav-wa-btn"
          >
            <FaWhatsapp size={16} />
            واتساب
          </a>
        )}

        <button className="pml-hamburger" onClick={() => setMenuOpen(true)} aria-label="فتح القائمة">
          <span /><span /><span />
        </button>
      </nav>

      {/* ── Mobile Menu ────────────────────────────────────────────────────── */}
      <div className={`pml-mobile-menu${menuOpen ? ' pml-mobile-menu--open' : ''}`}>
        <button className="pml-mobile-close" onClick={() => setMenuOpen(false)} aria-label="إغلاق">×</button>
        {navSections.map(section => (
          <a
            key={section.id}
            href={`#${section.id}`}
            onClick={e => { e.preventDefault(); scrollTo(section.id); }}
          >
            {section.label}
          </a>
        ))}
      </div>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section
        className="pml-hero"
        style={{ paddingTop: isPreview && showPreviewBanner ? '110px' : undefined }}
      >
        <div className="pml-hero-bg" style={{
          backgroundImage: heroBackgroundImage,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          animation: heroImageMode === 'none' ? 'none' : 'ken-burns 20s infinite alternate ease-in-out',
        }} />
        <div className="pml-hero-overlay" />
        <div className="pml-hero-content">
          {heroBadgeText && (
            <div className="pml-hero-badge">
              {heroBadgeText}
            </div>
          )}
          <h1 className="pml-animate pml-animate-slideDown pml-hero-name">{marketer?.name}</h1>
          {heroIntroText && (
            <p className="pml-animate pml-animate-slideDown pml-hero-bio" style={{ animationDelay: '0.2s' }}>
              {heroIntroText}
            </p>
          )}
          {/* Short trust line under the name to help visitors decide faster. */}
          {heroTrustItems.length > 0 && (
            <p className="pml-animate pml-animate-slideDown pml-hero-trust" style={{ animationDelay: '0.25s' }}>
              {heroTrustItems.join(' • ')}
            </p>
          )}
          <div className="pml-animate pml-animate-slideUp pml-hero-actions" style={{ animationDelay: '0.3s' }}>
            <button type="button" className="pml-btn-gold pml-btn-hero-primary" onClick={handleOpenRequestModal}>
              <Home size={18} />
              اطلب عقارك
            </button>
            <a href="#properties" className="pml-btn-gold pml-btn-hero-primary" onClick={e => { e.preventDefault(); scrollTo('properties'); }}>
              <Building2 size={18} />
              {heroBrowseText}
            </a>
            {marketer?.whatsapp && (
              <a
                href={`https://wa.me/${marketer.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="pml-btn-wa-outline"
              >
                <FaWhatsapp size={18} />
                تواصل واتساب
              </a>
            )}
            {hasWhatsappGroup && (
              <a
                href={whatsappGroupUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="pml-btn-group-outline"
              >
                <FaWhatsapp size={16} />
                {whatsappGroupLabel}
              </a>
            )}
            {showHeroConsultButton && marketer?.whatsapp && (
              <a
                href={`https://wa.me/${marketer.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="pml-btn-consult"
              >
                اطلب استشارة
              </a>
            )}
          </div>
        </div>
        <button
          className="pml-hero-scroll"
          onClick={() => firstVisibleSectionId && scrollTo(firstVisibleSectionId)}
          aria-label="تمرير للأسفل"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <ChevronDown size={32} />
        </button>
      </section>

      {showRequestModal && (
        <div className="pml-request-modal-overlay" onClick={handleCloseRequestModal}>
          <div className="pml-request-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="pml-request-close" onClick={handleCloseRequestModal} aria-label="إغلاق">
              <X size={20} />
            </button>
            {!requestResult ? (
              <form className="pml-request-form" onSubmit={handleSubmitPropertyRequest}>
                <div className="pml-request-head">
                  <span>طلب عقار</span>
                  <h2>خلّنا نعرف طلبك</h2>
                  <p>اكتب بياناتك الأساسية، وسيصل الطلب للمسوق بانتظار المراجعة.</p>
                </div>
                <div className="pml-request-grid">
                  <label>
                    الاسم *
                    <input value={propertyRequest.clientName} onChange={(e) => handlePropertyRequestChange('clientName', e.target.value)} />
                  </label>
                  <label>
                    رقم الجوال *
                    <input dir="ltr" value={propertyRequest.clientPhone} onChange={(e) => handlePropertyRequestChange('clientPhone', e.target.value)} placeholder="05xxxxxxxx" />
                  </label>
                  <label>
                    نوع الطلب *
                    <select value={propertyRequest.requestType} onChange={(e) => handlePropertyRequestChange('requestType', e.target.value)}>
                      <option value="buy">شراء</option>
                      <option value="rent">إيجار</option>
                    </select>
                  </label>
                  <label>
                    نوع العقار *
                    <select value={propertyRequest.propertyType} onChange={(e) => handlePropertyRequestChange('propertyType', e.target.value)}>
                      {REQUEST_PROPERTY_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                    </select>
                  </label>
                  <label>
                    المدينة *
                    {requestCityOptions.length > 0 ? (
                      <select value={propertyRequest.city} onChange={(e) => handlePropertyRequestChange('city', e.target.value)}>
                        <option value="">اختر المدينة</option>
                        {requestCityOptions.map(city => <option key={city} value={city}>{city}</option>)}
                      </select>
                    ) : (
                      <input value={propertyRequest.city} onChange={(e) => handlePropertyRequestChange('city', e.target.value)} />
                    )}
                  </label>
                  <label>
                    الحي أو الأحياء المطلوبة
                    <input value={propertyRequest.district} onChange={(e) => handlePropertyRequestChange('district', e.target.value)} />
                  </label>
                  <label>
                    الميزانية *
                    <input inputMode="numeric" value={propertyRequest.budget} onChange={(e) => handlePropertyRequestChange('budget', e.target.value)} />
                  </label>
                  <label>
                    المساحة المطلوبة
                    <input inputMode="numeric" value={propertyRequest.areaMin} onChange={(e) => handlePropertyRequestChange('areaMin', e.target.value)} />
                  </label>
                </div>
                <label className="pml-request-notes">
                  ملاحظات
                  <textarea rows={3} value={propertyRequest.notes} onChange={(e) => handlePropertyRequestChange('notes', e.target.value)} />
                </label>
                {requestError && <p className="pml-request-error">{requestError}</p>}
                <button type="submit" className="pml-request-submit" disabled={requestSubmitting || isPreview}>
                  {requestSubmitting ? <Loader size={18} className="pml-spin" /> : <Send size={18} />}
                  إرسال الطلب
                </button>
              </form>
            ) : (
              <div className="pml-request-success">
                <h2>{requestSuccessMessage}</h2>
                {requestResult.matchingOffers?.length > 0 && (
                  <div className="pml-request-offers">
                    {requestResult.matchingOffers.map(offer => {
                      const offerLink = offer.catalogSlug
                        ? getMarketerCatalogPath({ slug: offer.catalogSlug }, offer._id)
                        : '#properties';
                      return (
                        <Link key={offer._id} to={offerLink} className="pml-request-offer-card">
                          {offer.coverImage ? <img src={offer.coverImage} alt={offer.title} /> : <div className="pml-request-offer-placeholder"><Building2 size={24} /></div>}
                          <div>
                            <strong>{offer.title || 'عرض عقاري'}</strong>
                            <span>{offer.city}{offer.district ? ` - ${offer.district}` : ''}</span>
                            <small>{Number(offer.price) > 0 ? `${Number(offer.price).toLocaleString('ar-SA')} ريال` : 'السعر عند التواصل'}</small>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
                <button type="button" className="pml-request-submit" onClick={handleCloseRequestModal}>تم</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* About now appears directly after Hero, before services and properties. */}
      {marketer?.showAboutSection !== false && (
        <section id="about" className={`pml-section pml-about ${getMiddleSectionToneClass()}`}>
          <div className="pml-section-inner">
            <div ref={aboutRef} className="fade-up pml-about-grid">
              <div className="pml-about-left">
                <div className="pml-animate pml-animate-fadeUp pml-about-avatar" style={{ animationDelay: '0.1s' }}>
                  {marketerAvatarSrc ? (
                    <img src={marketerAvatarSrc} alt={marketer?.name || 'الشعار أو صورة الحساب'} onError={() => setAvatarFailed(true)} />
                  ) : (
                    marketer?.name?.charAt(0)
                  )}
                </div>
                <p className="pml-animate pml-animate-fadeUp pml-about-fullname" style={{ animationDelay: '0.2s' }}>{marketer?.name}</p>
                {/* Keep specialty as a small badge only when it exists. */}
                {marketer?.specialty && (
                  <span className="pml-about-title-badge">{marketer.specialty}</span>
                )}
                {marketer?.falLicense && (
                  <p className="pml-about-fal">رخصة فال: <span>{marketer.falLicense}</span></p>
                )}
              </div>
              <div>
                <p className="pml-section-label">نبذة عني</p>
                <h2 className="pml-animate pml-animate-fadeUp pml-section-title">من أنا؟</h2>
                {/* Default bio keeps the section from looking empty when bio is missing. */}
                <p className="pml-animate pml-animate-fadeUp pml-about-bio" style={{ animationDelay: '0.3s' }}>{aboutBio}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Services appear directly after Hero when the marketer has saved services. */}
      {hasServicesSection && (
        <section id="services" className={`pml-section pml-services ${getMiddleSectionToneClass()}`}>
          <div className="pml-section-inner">
            <h2 className="pml-animate pml-animate-fadeUp pml-section-title">كيف أقدر أساعدك؟</h2>
            <p className="pml-section-sub">اختر الخدمة المناسبة وابدأ التواصل مباشرة.</p>
            <div className="pml-animate pml-animate-fadeUp pml-services-list">
              {marketerServices.map((service, index) => {
                const serviceWhatsappLink = getServiceWhatsappLink(service);
                return serviceWhatsappLink ? (
                  <a
                    key={`${service}-${index}`}
                    className="pml-service-pill pml-service-pill--clickable"
                    href={serviceWhatsappLink}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span>{service}</span>
                    <ChevronLeft size={14} />
                  </a>
                ) : (
                  <span key={`${service}-${index}`} className="pml-service-pill">
                    {service}
                  </span>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Available properties combines catalogs and latest offers in one section. */}
      {hasAvailablePropertiesData && (
        <section id="properties" className={`pml-section pml-properties pml-available-properties ${getMiddleSectionToneClass()}`}>
          <div className="pml-section-inner">
            <h2 className="pml-animate pml-animate-fadeUp pml-section-title">العقارات المتاحة</h2>
            <p className="pml-section-sub">تصفح كل العروض أو اختر مجموعة عقارية تناسبك.</p>

            {showOffersInAvailableProperties && offers.length > 0 && allOffersCatalog && (
              <div className="pml-center pml-available-primary">
                <a
                  href={getMarketerCatalogPath(allOffersCatalog)}
                  className="pml-btn-outline-dark"
                >
                      <span>عرض كل العقارات</span>
                  <ChevronLeft size={16} />
                </a>
              </div>
            )}

            {showCatalogsInAvailableProperties && customCatalogs.length > 0 && (
              <div className="pml-available-block">
                <h3 className="pml-subsection-title">مجموعات مختارة</h3>
                <div ref={catalogRef} className="fade-up pml-catalogs-grid">
                  {customCatalogs.map((catalog, i) => (
                    <a
                      key={catalog._id || i}
                      href={getMarketerCatalogPath(catalog)}
                      className={`pml-animate pml-animate-fadeUp pml-card-lift pml-card pml-catalog-card stagger-${Math.min(i + 1, 6)}`}
                      style={{ textDecoration: 'none', color: 'inherit', display: 'block', animationDelay: `${0.1 + Math.min(i, 5) * 0.1}s` }}
                    >
                      <div className="pml-catalog-thumb pml-neutral-placeholder">
                        <BookOpen size={36} />
                        <span>كتالوج عقاري</span>
                      </div>
                      <div className="pml-catalog-body">
                        <p className="pml-catalog-name">{catalog.name}</p>
                        <div className="pml-catalog-meta">
                          <span className="pml-catalog-count">{catalog.offersCount || 0} عقار</span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {showOffersInAvailableProperties && (
            <div className="pml-available-block">
              <h3 className="pml-subsection-title">أحدث العروض</h3>

              {availableFilters.length > 1 && (
                <div className="pml-filter-bar">
                  {availableFilters.map(f => (
                    <button
                      key={f.value}
                      className={`pml-pill${activeFilter === f.value ? ' active' : ''}`}
                      onClick={() => setActiveFilter(f.value)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}

              {filteredProps.length === 0 ? (
                <div className="pml-empty-section">
                  <p>{activeFilter === 'all' ? 'لا توجد عقارات متاحة حالياً' : 'لا توجد عروض ضمن هذا التصنيف حاليًا.'}</p>
                </div>
              ) : (
                <>
                <div ref={propsRef} className="fade-up pml-props-grid">
                  {filteredProps.map((offer, i) => {
                    const targetCatalog = getCatalogForOffer(offer);
                    return (
                      <div
                        key={offer._id || i}
                        className={`pml-animate pml-animate-fadeUp pml-card-lift pml-card pml-prop-card stagger-${Math.min(i + 1, 6)}`}
                        style={{ animationDelay: `${0.1 + Math.min(i, 5) * 0.1}s`, cursor: targetCatalog ? 'pointer' : 'default' }}
                        onClick={targetCatalog ? () => navigate(getMarketerCatalogPath(targetCatalog, offer._id)) : undefined}
                      >
                        <div className="pml-prop-thumb">
                          {offer.coverImage ? (
                            <img src={offer.coverImage} alt={offer.title}
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                          ) : (
                            <div className="pml-prop-placeholder">
                              <Building2 size={30} />
                              <span>{offerTypeLabel(offer.propertyType) || 'لا توجد صورة'}</span>
                            </div>
                          )}
                          <span className="pml-prop-type-badge">{offerTypeLabel(offer.propertyType)}</span>
                        </div>
                        <div className="pml-prop-body">
                          <p className="pml-prop-price">{offer.price?.toLocaleString()} ريال</p>
                          <p className="pml-prop-title">{offer.title}</p>
                          <div className="pml-prop-meta">
                            <span className="pml-prop-meta-item"><MapPin size={13} />{offer.district || offer.city}</span>
                            <span className="pml-prop-meta-item"><Maximize size={13} />{offer.area} م²</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Mobile keeps a catalog shortcut under the horizontal offers slider. */}
                {offers.length > 0 && allOffersCatalog && (
                  <div className="pml-center pml-mobile-slider-more">
                    <a
                      href={getMarketerCatalogPath(allOffersCatalog)}
                      className="pml-btn-outline-dark"
                    >
                  <span>عرض كل العقارات</span>
                      <ChevronLeft size={16} />
                    </a>
                  </div>
                )}
                </>
              )}
            </div>
            )}
            {hasWhatsappGroup && (
              <div className="pml-properties-group-cta">
                <a
                  href={whatsappGroupUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pml-btn-group-soft"
                >
                  <FaWhatsapp size={16} />
                  {whatsappGroupLabel}
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Specialty & Areas ──────────────────────────────────────────────── */}
      {hasSpecialtySection && (
        <section id="specialty" className={`pml-section pml-specialty ${getMiddleSectionToneClass()}`}>
          <div className="pml-section-inner">
            <p className="pml-section-label">النطاق</p>
            <h2 className="pml-animate pml-animate-fadeUp pml-section-title">نطاق العمل</h2>
            <p className="pml-section-sub">المناطق وأنواع العقارات التي يغطيها هذا الحساب</p>
            <div ref={specRef} className="fade-up pml-specialty-grid">
                {marketer?.specialty && (
                  <div className="pml-animate pml-animate-fadeUp pml-spec-card stagger-1" style={{ animationDelay: '0.1s' }}>
                    <div className="pml-spec-icon"><Home size={22} /></div>
                    <p className="pml-spec-label">نوع العقارات</p>
                    <p className="pml-spec-value">{marketer.specialty}</p>
                  </div>
                )}
                {marketer?.districts && (
                  <div className="pml-animate pml-animate-fadeUp pml-spec-card stagger-2" style={{ animationDelay: '0.2s' }}>
                    <div className="pml-spec-icon"><MapPin size={22} /></div>
                    <p className="pml-spec-label">المناطق</p>
                    <p className="pml-spec-value">{marketer.districts}</p>
                  </div>
                )}
                {((marketer?.priceRangeMin > 0) || (marketer?.priceRangeMax > 0)) && (
                  <div className="pml-animate pml-animate-fadeUp pml-spec-card stagger-3" style={{ animationDelay: '0.3s' }}>
                    <div className="pml-spec-icon"><DollarSign size={22} /></div>
                    <p className="pml-spec-label">نطاق الأسعار</p>
                    <p className="pml-spec-value">
                      {marketer?.priceRangeMin > 0 && marketer?.priceRangeMax > 0
                        ? `${marketer.priceRangeMin.toLocaleString()} — ${marketer.priceRangeMax.toLocaleString()} ريال`
                        : marketer?.priceRangeMin > 0
                          ? `من ${marketer.priceRangeMin.toLocaleString()} ريال`
                          : `حتى ${marketer.priceRangeMax.toLocaleString()} ريال`}
                    </p>
                  </div>
                )}
                {marketer?.languages && (
                  <div className="pml-animate pml-animate-fadeUp pml-spec-card stagger-4" style={{ animationDelay: '0.4s' }}>
                    <div className="pml-spec-icon"><Globe size={22} /></div>
                    <p className="pml-spec-label">اللغات</p>
                    <p className="pml-spec-value">{marketer.languages}</p>
                  </div>
                )}
            </div>
          </div>
        </section>
      )}

      {/* ── Stats Bar ──────────────────────────────────────────────────────── */}
      {hasStatsSection && (
        <section id="stats" className={`pml-section pml-stats ${getMiddleSectionToneClass()}`}>
          <div ref={statsRef} className="fade-up pml-stats-grid">
            {visibleStats.map((stat, index) => (
              <div
                key={stat.key}
                className="pml-animate pml-animate-fadeUp pml-stat-item"
                style={{ animationDelay: `${0.1 + index * 0.1}s` }}
              >
                <AnimatedNumber target={stat.target} suffix={stat.suffix} />
                <div className="pml-stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Contact ────────────────────────────────────────────────────────── */}
      {hasContactSection && (
        <section id="contact" className="pml-section pml-contact">
          <div className="pml-section-inner">
            <p className="pml-section-label" style={{ color: 'var(--pml-gold)' }}>تواصل</p>
            <h2 className="pml-animate pml-animate-fadeUp pml-section-title">تواصل مع {marketer?.name}</h2>
            <p className="pml-section-sub">جاهز لاستقبال استفساراتك العقارية.</p>

            <div ref={contactRef} className="fade-up pml-contact-grid">
              {hasPhone && (
                <a href={`tel:${marketer.phone}`}
                  className="pml-animate pml-animate-fadeUp pml-contact-card" style={{animationDelay:'0.1s', textDecoration:'none', color:'inherit'}}>
                  <div className="pml-contact-icon"><Phone size={24} /></div>
                  <p className="pml-contact-label">جوال</p>
                  <p className="pml-contact-value">{marketer.phone}</p>
                </a>
              )}
              {hasEmail && (
                <a href={`mailto:${marketer.email}`}
                  className="pml-animate pml-animate-fadeUp pml-contact-card" style={{animationDelay:'0.2s', textDecoration:'none', color:'inherit'}}>
                  <div className="pml-contact-icon"><Mail size={24} /></div>
                  <p className="pml-contact-label">بريد إلكتروني</p>
                  <p className="pml-contact-value">{marketer.email}</p>
                </a>
              )}
              {hasTwitter && (
                <a href={`https://twitter.com/${marketer.twitter.replace('@','')}`} target="_blank" rel="noreferrer"
                  className="pml-animate pml-animate-fadeUp pml-contact-card" style={{animationDelay:'0.3s', textDecoration:'none', color:'inherit'}}>
                  <div className="pml-contact-icon"><Twitter size={24} /></div>
                  <p className="pml-contact-label">تويتر</p>
                  <p className="pml-contact-value">{marketer.twitter}</p>
                </a>
              )}
              {hasInstagram && (
                <a href={normalizeExternalUrl(marketer.instagram, 'https://instagram.com/')} target="_blank" rel="noreferrer"
                  className="pml-animate pml-animate-fadeUp pml-contact-card" style={{animationDelay:'0.4s', textDecoration:'none', color:'inherit'}}>
                  <div className="pml-contact-icon"><FaInstagram size={24} /></div>
                  <p className="pml-contact-label">Instagram</p>
                  <p className="pml-contact-value">{marketer.instagram}</p>
                </a>
              )}
              {hasTiktok && (
                <a href={normalizeExternalUrl(marketer.tiktok, 'https://www.tiktok.com/@')} target="_blank" rel="noreferrer"
                  className="pml-animate pml-animate-fadeUp pml-contact-card" style={{animationDelay:'0.5s', textDecoration:'none', color:'inherit'}}>
                  <div className="pml-contact-icon"><FaTiktok size={24} /></div>
                  <p className="pml-contact-label">TikTok</p>
                  <p className="pml-contact-value">{marketer.tiktok}</p>
                </a>
              )}
              {hasExtraLink && (
                <a href={normalizeExternalUrl(marketer.extraLink)} target="_blank" rel="noreferrer"
                  className="pml-animate pml-animate-fadeUp pml-contact-card" style={{animationDelay:'0.6s', textDecoration:'none', color:'inherit'}}>
                  <div className="pml-contact-icon"><Globe size={24} /></div>
                  <p className="pml-contact-label">رابط إضافي</p>
                  <p className="pml-contact-value">{marketer.extraLink}</p>
                </a>
              )}
              {hasWhatsappGroup && (
                <a href={whatsappGroupUrl} target="_blank" rel="noreferrer"
                  className="pml-animate pml-animate-fadeUp pml-contact-card" style={{animationDelay:'0.7s', textDecoration:'none', color:'inherit'}}>
                  <div className="pml-contact-icon"><FaWhatsapp size={24} /></div>
                  <p className="pml-contact-label">قروب واتساب</p>
                  <p className="pml-contact-value">{whatsappGroupLabel}</p>
                </a>
              )}
              {publicAdditionalContacts.map((contact, index) => (
                <div
                  key={`${contact.phone}-${index}`}
                  className="pml-animate pml-animate-fadeUp pml-contact-card pml-extra-contact-card"
                  style={{animationDelay:`${0.8 + index * 0.1}s`}}
                >
                  <div className="pml-contact-icon"><Phone size={24} /></div>
                  <p className="pml-contact-label">{contact.label || 'رقم تواصل'}</p>
                  <p className="pml-contact-value">{normalizeContactPhone(contact.phone)}</p>
                  <div className="pml-extra-contact-actions">
                    {contact.whatsappEnabled !== false && (
                      <a href={`https://wa.me/${getWhatsappPhone(contact.phone)}`} target="_blank" rel="noreferrer">
                        <FaWhatsapp size={15} />
                        واتساب
                      </a>
                    )}
                    {contact.callEnabled === true && (
                      <a href={`tel:${getTelPhone(contact.phone)}`}>
                        <Phone size={15} />
                        اتصال
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {Boolean(marketer?.whatsapp) && (
              <div className="pml-center" style={{ marginTop: '0' }}>
                <a
                  href={`https://wa.me/${marketer.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pml-btn-wa-large"
                >
                  <FaWhatsapp size={24} />
                  تواصل عبر واتساب
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="pml-footer">
        <p className="pml-footer-brand">صفحة مقدمة عبر منصة مسك</p>
        <a className="pml-footer-url" href={`/p/${marketer?.username || username}`}>
          miskest.com/p/{marketer?.username || username}
        </a>
        <div className="pml-footer-links">
          <a href="/privacy" target="_blank" rel="noreferrer">سياسة الخصوصية</a>
          <span>·</span>
          <a href="/terms" target="_blank" rel="noreferrer">شروط الاستخدام</a>
          <span>·</span>
          <a href="https://miskest.com" target="_blank" rel="noreferrer">عن مسك</a>
        </div>
      </footer>

      {scrolled && (
        <button
          type="button"
          className="pml-scroll-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="العودة للأعلى"
          title="العودة للأعلى"
        >
          <ArrowUp size={20} />
        </button>
      )}

    </div>
  );
}
